import React from 'react';
import {
  Checkbox,
  CheckboxProps,
  FormControlLabel,
  FormControlLabelProps,
} from '@mui/material';

/**
 * Props for the ControlledCheckbox component.
 * Extends MUI's CheckboxProps, omitting 'checked' and 'onChange'.
 */
interface ControlledCheckboxProps
  extends Omit<CheckboxProps, 'checked' | 'onChange'> {
  /** Current checkbox state */
  checkedValue: boolean;
  /** Function to update checkbox state */
  setCheckedValue: React.Dispatch<React.SetStateAction<boolean>>;
  /** Optional label to display next to checkbox */
  label?: React.ReactNode;
  /** Props for the FormControlLabel wrapper (if label is provided) */
  formControlLabelProps?: Partial<FormControlLabelProps>;
}

/**
 * A controlled checkbox component using MUI's Checkbox.
 * This component provides a customizable and reusable input with controlled state.
 *
 * @param {ControlledCheckboxProps} props - The props for the component
 * @returns {React.ReactElement} The rendered ControlledCheckbox component
 */
const ControlledCheckbox: React.FC<ControlledCheckboxProps> = ({
  checkedValue,
  setCheckedValue,
  label,
  formControlLabelProps,
  ...props
}) => {
  const checkbox = (
    <Checkbox
      {...props}
      checked={checkedValue}
      onChange={(e) => setCheckedValue(e.target.checked)}
    />
  );

  return label ? (
    <FormControlLabel
      control={checkbox}
      label={label}
      {...formControlLabelProps}
    />
  ) : (
    checkbox
  );
};

export default ControlledCheckbox;
